import { useState, useEffect, useCallback } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import {
    fetchQnbPOSSettings,
    upsertQnbPOSSettings,
    deleteQnbPOSSettings
} from "../../../api/requests/company-requests";

export const useQnbPaymentSettings = (companyId) => {
    const [loading, setLoading] = useState(false);
    const [hasSettings, setHasSettings] = useState(false);
    const [settingsId, setSettingsId] = useState(null);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            companyId: companyId || "",
            merchantId: "",
            userCode: "",
            userPass: "",
            merchantPass: "",
            testMode: false
        },
        validationSchema: Yup.object({
            companyId: Yup.string().required("Gerekli"),
            merchantId: Yup.string().required("Merchant ID gerekli"),
            userCode: Yup.string().required("Kullanıcı kodu gerekli"),
            userPass: Yup.string().required("Kullanıcı şifresi gerekli"),
            merchantPass: Yup.string().required("Merchant şifresi (StoreKey) gerekli")
        }),
        onSubmit: async (values) => {
            setLoading(true);
            try {
                const payload = {
                    companyId: values.companyId,
                    merchantId: values.merchantId,
                    userCode: values.userCode,
                    userPass: values.userPass,
                    merchantPass: values.merchantPass,
                    testMode: values.testMode
                };

                const res = await upsertQnbPOSSettings(payload);
                if (res?.success || res?.data) {
                    setHasSettings(true);
                    if (res?.data?.id) setSettingsId(res.data.id);
                    toast.success("QNB PayFor ayarları güncellendi");
                } else {
                    toast.error(res?.message || "QNB ayarları kaydedilemedi");
                }
            } catch (error) {
                toast.error("QNB ayarları kaydedilirken hata oluştu");
            } finally {
                setLoading(false);
            }
        }
    });

    useEffect(() => {
        if (!companyId) return;

        const loadSettings = async () => {
            try {
                const res = await fetchQnbPOSSettings(companyId);

                if (res?.data) {
                    setHasSettings(true);
                    setSettingsId(res.data.id ?? null);
                    // Güvenlik: userPass ve merchantPass backend'den DÖNMEZ; boş bırakılır,
                    // güncelleme için tekrar girilmeleri gerekir.
                    formik.setValues({
                        companyId,
                        merchantId: res.data.merchantId ?? "",
                        userCode: res.data.userCode ?? "",
                        userPass: "",
                        merchantPass: "",
                        testMode: res.data.testMode ?? false
                    });
                } else {
                    setHasSettings(false);
                    setSettingsId(null);
                }
            } catch (error) {
                console.error("QNB ayarları yüklenemedi", error);
                setHasSettings(false);
                setSettingsId(null);
            }
        };

        loadSettings();
    }, [companyId]);

    // Bağlantıyı kaldır: backend'deki kaydı siler ama girilen form bilgilerini
    // olduğu gibi bırakır (tekrar bağlanmak isterse yeniden yazmasın diye).
    const disconnect = useCallback(async () => {
        // Henüz kaydedilmemiş (yerel) bağlantı → sadece bağlı durumunu düşür.
        if (!settingsId) {
            setHasSettings(false);
            return true;
        }
        try {
            const res = await deleteQnbPOSSettings(settingsId);
            if (res?.statusCode === 200 || res?.data === true) {
                setHasSettings(false);
                setSettingsId(null);
                return true;
            }
            return false;
        } catch (error) {
            console.error("QNB bağlantısı kaldırılamadı", error);
            return false;
        }
    }, [settingsId]);

    return { formik, loading, hasSettings, disconnect };
};
